import { gql } from '@apollo/client';

import { formatCurrency } from '@/lib/utils';

export const GET_ACCOUNT_OTHERS = gql`
  query GetAccountOthers($id: ID!) {
    node(id: $id) {
      ... on AccountNode {
        id
        entityImageId
        createUid {
          id
          name
        }
        createDate
        writeUid {
          id
          name
        }
        writeDate
        modifiedByDelegate {
          id
          name
        }
        geocodeRecord
        importSequenceNumber
        soundex
        commitmentAmount
        commitmentAmountBase
        lpInvestmentAmmountMin
        lpInvestmentAmmountMinBase
        lpInvestmentAmmountMax
        lpInvestmentAmmountMaxBase
        assetsUnderManagement
        assetsUnderManagementBase
        annualRevenue
        annualRevenueBase
        openRevenue
        openRevenueBase
        openRevenueLastUpdatedTime
        # AccountNode fields
        lpConsultantGeneralId {
          id
          name
        }
        lpConsultantId {
          id
          name
        }
        cais
        dateLpPlatformSuppressUnchecked
        reasonForUncheckedLpPlatformSuppress
        investorPlacedCount
        totalActivities
        totalContacts
        totalContactsLastUpdatedOn
        totalInvestorOpportunities
        totalInvestorOpportunitiesLastUpdatedOn
        totalOpenIos
        totalNotes
        creditMarkYes
        openDeals
        numberOfEmployees
        warmIntroProcessCount
        warmIntroProcessDate
        lastActivityAddedOn
        lastNoteAddedOn
        lastSynchedToNotion
        ownership
        ownerId {
          id
          name
        }
        owningUser {
          id
          name
        }
        placementAgent
        placementAgentDateAssigned
        placementAgentGlobal
        preferredEmailAddress
        preferredMethodOfContact
        followEmailActivity
        gdprOptout
        reverseSolicitationEmailReceivedDate
        division
        sicCode
        fundManagerType
        tickerSymbol
        crmUrl
        ndaSignedDate
        summaryNotes
        nextStepsPlugin
        indiaQaCheckNeeded
        indiaQaCheckedBy {
          id
          name
        }
        indiaQaDate
        india2QaCheckNeeded
        lastIndia2QaCheckedBy {
          id
          name
        }
        lastIndiaQaCheckedBy {
          id
          name
        }
        india2QaCheckedBy {
          id
          name
        }
        india2QaDate
        ny2QaCheckNeeded
        bdc
        esgFocused
        nonSponsorTransactions
        sbicQualifying
        sponsorTransactions
        sponsorInformation
        investorCount
        isIcapitalPlatform
        revolverNonAbl
        preqinFirmId
        sqlDate
        typeOfInvestor
        primaryContactOriginal
        nameWithParent
        isCais
        fax
        limitedPartner
        parentAccountId {
          id
          name
        }
        mainPhone
        openDealsLastUpdatedTime
        marketingPreferencesIds {
          id
          accountId {
            id
            name
          }
          allowTextMessages
          doNotAllowBulkEmails
          doNotAllowEmails
          doNotAllowFaxes
          doNotAllowPhoneCalls
          sendMarketingMaterials
          marketingOnly
        }
      }
    }
  }
`;

// UI Data Type
export interface AccountOthersData {
  accountId: string;
  annualRevenue: string;
  annualRevenueBase: string;
  assetsUnderManagement: string;
  assetsUnderManagementBase: string;
  bdc: boolean;
  caes: boolean;
  commitmentAmount: string;
  commitmentAmountBase: string;
  createdBy: string;
  createdOn: string;
  creditMarkYes: string;
  crmUrl: string;
  dateLpPlatformSuppressUnchecked: string;
  division: string;
  entityImageId: string;
  esgFocused: boolean;
  fax: string;
  followEmailActivity: boolean;
  fundManagerType: string;
  gdprOptout: boolean;
  geocodeRecord: boolean;
  iCapital: boolean;
  importSequenceNumber: null | number;
  india2QaCheckedBy: string;
  india2QaCheckedById: string;
  india2QaCheckNeeded: boolean;
  indiaQaCheckedBy: string;
  indiaQaCheckedById: string;
  indiaQaCheckNeeded: boolean;
  investorCount: null | number;
  isCais: boolean;
  isIcapitalPlatform: boolean;
  lastActivityAddedOn: string;
  lastIndia2QaCheckedBy: string;
  lastIndia2QaCheckedById: string;
  lastIndia2QaDate: string;
  lastIndiaQaCheckedBy: string;
  lastIndiaQaCheckedById: string;
  lastIndiaQaDate: string;
  lastNoteAddedOn: string;
  lastSynchedToNotion: string;
  limitedPartner: boolean;
  lpConsultantGeneral: string;
  lpConsultantGeneralId: string;
  lpConsultantPe: string;
  lpConsultantPeId: string;
  lpInvestmentAmountMax: string;
  lpInvestmentAmountMaxBase: string;
  lpInvestmentAmountMin: string;
  lpInvestmentAmountMinBase: string;
  mainPhone: string;
  marketingPreferences: MarketingPreferences;
  modifiedBy: string;
  modifiedByDelegate: string;
  modifiedOn: string;
  nameWithParent: string;
  ndaSignedDate: string;
  nextStepsPlugin: string;
  nonSponsorTransactions: boolean;
  numberOfEmployees: null | number;
  ny2QaCheckNeeded: boolean;
  openDeals: null | number;
  openDealsLastUpdatedTime: string;
  openRevenue: string;
  openRevenueBase: string;
  openRevenueLastUpdatedTime: string;
  owner: string;
  ownerId: string;
  ownership: string;
  owningUser: string;
  owningUserId: string;
  parentAccountId: string;
  placementAgent: string;
  placementAgentDateAssigned: string;
  placementAgentGlobal: string;
  preferredEmailAddress: string;
  preferredMethodOfContact: string;
  preqinFirmId: string;
  primaryContactOriginal: string;
  qaCheckCount: string;
  reasonForUncheckedLpPlatformSuppress: string;
  reverseSolicitationEmailReceivedDate: string;
  revolverNonAbl: boolean;
  sbicQualifying: boolean;
  sicCode: string;
  soundex: string;
  sponsorInformation: string;
  sponsorTransactions: boolean;
  sqlDate: string;
  summaryNotes: string;
  tickerSymbol: string;
  totalActivities: null | number;
  totalContacts: null | number;
  totalContactsLastUpdatedOn: string;
  totalInvestorOpportunities: null | number;
  totalInvestorOpportunitiesLastUpdatedOn: string;
  totalNotes: null | number;
  totalOpenIos: null | number;
  typeOfInvestor: string;
  warmIntroProcessCount: null | number;
  warmIntroProcessDate: string;
}

// GraphQL Response Types
export interface AccountOthersNode {
  annualRevenue?: null | number;
  annualRevenueBase?: null | number;
  assetsUnderManagement?: null | number;
  assetsUnderManagementBase?: null | number;
  bdc?: boolean | null;
  cais?: boolean | null;
  commitmentAmount?: null | number;
  commitmentAmountBase?: null | number;
  createDate?: null | string;
  createUid?: { id: string; name: string } | null;
  creditMarkYes?: null | string;
  crmUrl?: null | string;
  dateLpPlatformSuppressUnchecked?: null | string;
  division?: null | string;
  entityImageId?: null | string;
  esgFocused?: boolean | null;
  fax?: null | string;
  followEmailActivity?: boolean | null;
  fundManagerType?: null | string;
  gdprOptout?: boolean | null;
  geocodeRecord?: boolean | null;
  id: string;
  importSequenceNumber?: null | number;
  india2QaCheckedBy?: { id: string; name: string } | null;
  india2QaCheckNeeded?: boolean | null;
  india2QaDate?: null | string;
  indiaQaCheckedBy?: { id: string; name: string } | null;
  indiaQaCheckNeeded?: boolean | null;
  indiaQaDate?: null | string;
  investorCount?: null | number;
  investorPlacedCount?: null | number;
  isCais?: boolean | null;
  isIcapitalPlatform?: boolean | null;
  lastActivityAddedOn?: null | string;
  lastIndia2QaCheckedBy?: { id: string; name: string } | null;
  lastIndiaQaCheckedBy?: { id: string; name: string } | null;
  lastNoteAddedOn?: null | string;
  lastSynchedToNotion?: null | string;
  limitedPartner?: boolean | null;
  lpConsultantGeneralId?: { id: string; name: string } | null;
  lpConsultantId?: { id: string; name: string } | null;
  lpInvestmentAmmountMax?: null | number;
  lpInvestmentAmmountMaxBase?: null | number;
  lpInvestmentAmmountMin?: null | number;
  lpInvestmentAmmountMinBase?: null | number;
  mainPhone?: null | string;
  marketingPreferencesIds?: MarketingPreferencesNode[] | null;
  modifiedByDelegate?: { id: string; name: string } | null;
  nameWithParent?: null | string;
  ndaSignedDate?: null | string;
  nextStepsPlugin?: null | string;
  nonSponsorTransactions?: boolean | null;
  numberOfEmployees?: null | number;
  ny2QaCheckNeeded?: boolean | null;
  openDeals?: null | number;
  openDealsLastUpdatedTime?: null | string;
  openRevenue?: null | number;
  openRevenueBase?: null | number;
  openRevenueLastUpdatedTime?: null | string;
  ownerId?: { id: string; name: string } | null;
  ownership?: null | string;
  owningUser?: { id: string; name: string } | null;
  parentAccountId?: { id: string; name: string } | null;
  placementAgent?: null | string;
  placementAgentDateAssigned?: null | string;
  placementAgentGlobal?: null | string;
  preferredEmailAddress?: null | string;
  preferredMethodOfContact?: null | string;
  preqinFirmId?: null | string;
  primaryContactOriginal?: null | string;
  reasonForUncheckedLpPlatformSuppress?: null | string;
  reverseSolicitationEmailReceivedDate?: null | string;
  revolverNonAbl?: boolean | null;
  sbicQualifying?: boolean | null;
  sicCode?: null | string;
  soundex?: null | string;
  sponsorInformation?: null | string;
  sponsorTransactions?: boolean | null;
  sqlDate?: null | string;
  summaryNotes?: null | string;
  tickerSymbol?: null | string;
  totalActivities?: null | number;
  totalContacts?: null | number;
  totalContactsLastUpdatedOn?: null | string;
  totalInvestorOpportunities?: null | number;
  totalInvestorOpportunitiesLastUpdatedOn?: null | string;
  totalNotes?: null | number;
  totalOpenIos?: null | number;
  typeOfInvestor?: null | string;
  warmIntroProcessCount?: null | number;
  warmIntroProcessDate?: null | string;
  writeDate?: null | string;
  writeUid?: { id: string; name: string } | null;
}

export interface GetAccountOthersData {
  node: AccountOthersNode | null;
}

export interface GetAccountOthersVariables {
  id: string;
}

export interface MarketingPreferences {
  allowTextMessages: boolean;
  doNotAllowBulkEmails: boolean;
  doNotAllowEmails: boolean;
  doNotAllowFaxes: boolean;
  doNotAllowPhoneCalls: boolean;
  marketingOnly: boolean;
  sendMarketingMaterials: boolean;
}

export interface MarketingPreferencesNode {
  accountId?: { id: string; name: string } | null;
  allowTextMessages?: boolean | null;
  doNotAllowBulkEmails?: boolean | null;
  doNotAllowEmails?: boolean | null;
  doNotAllowFaxes?: boolean | null;
  doNotAllowPhoneCalls?: boolean | null;
  id: string;
  marketingOnly?: boolean | null;
  sendMarketingMaterials?: boolean | null;
}

export function createEmptyAccountOthersData(): AccountOthersData {
  return {
    accountId: '',
    annualRevenue: '',
    annualRevenueBase: '',
    assetsUnderManagement: '',
    assetsUnderManagementBase: '',
    bdc: false,
    caes: false,
    commitmentAmount: '',
    commitmentAmountBase: '',
    createdBy: '',
    createdOn: '',
    creditMarkYes: '',
    crmUrl: '',
    dateLpPlatformSuppressUnchecked: '',
    division: '',
    entityImageId: '',
    esgFocused: false,
    fax: '',
    followEmailActivity: false,
    fundManagerType: '',
    gdprOptout: false,
    geocodeRecord: false,
    iCapital: false,
    importSequenceNumber: null,
    india2QaCheckedBy: '',
    india2QaCheckedById: '',
    india2QaCheckNeeded: false,
    indiaQaCheckedBy: '',
    indiaQaCheckedById: '',
    indiaQaCheckNeeded: false,
    investorCount: null,
    isCais: false,
    isIcapitalPlatform: false,
    lastActivityAddedOn: '',
    lastIndia2QaCheckedBy: '',
    lastIndia2QaCheckedById: '',
    lastIndia2QaDate: '',
    lastIndiaQaCheckedBy: '',
    lastIndiaQaCheckedById: '',
    lastIndiaQaDate: '',
    lastNoteAddedOn: '',
    lastSynchedToNotion: '',
    limitedPartner: false,
    lpConsultantGeneral: '',
    lpConsultantGeneralId: '',
    lpConsultantPe: '',
    lpConsultantPeId: '',
    lpInvestmentAmountMax: '',
    lpInvestmentAmountMaxBase: '',
    lpInvestmentAmountMin: '',
    lpInvestmentAmountMinBase: '',
    mainPhone: '',
    marketingPreferences: {
      allowTextMessages: false,
      doNotAllowBulkEmails: false,
      doNotAllowEmails: false,
      doNotAllowFaxes: false,
      doNotAllowPhoneCalls: false,
      marketingOnly: false,
      sendMarketingMaterials: false
    },
    modifiedBy: '',
    modifiedByDelegate: '',
    modifiedOn: '',
    nameWithParent: '',
    ndaSignedDate: '',
    nextStepsPlugin: '',
    nonSponsorTransactions: false,
    numberOfEmployees: null,
    ny2QaCheckNeeded: false,
    openDeals: null,
    openDealsLastUpdatedTime: '',
    openRevenue: '',
    openRevenueBase: '',
    openRevenueLastUpdatedTime: '',
    owner: '',
    ownerId: '',
    ownership: '',
    owningUser: '',
    owningUserId: '',
    parentAccountId: '',
    placementAgent: '',
    placementAgentDateAssigned: '',
    placementAgentGlobal: '',
    preferredEmailAddress: '',
    preferredMethodOfContact: '',
    preqinFirmId: '',
    primaryContactOriginal: '',
    qaCheckCount: '',
    reasonForUncheckedLpPlatformSuppress: '',
    reverseSolicitationEmailReceivedDate: '',
    revolverNonAbl: false,
    sbicQualifying: false,
    sicCode: '',
    soundex: '',
    sponsorInformation: '',
    sponsorTransactions: false,
    sqlDate: '',
    summaryNotes: '',
    tickerSymbol: '',
    totalActivities: null,
    totalContacts: null,
    totalContactsLastUpdatedOn: '',
    totalInvestorOpportunities: null,
    totalInvestorOpportunitiesLastUpdatedOn: '',
    totalNotes: null,
    totalOpenIos: null,
    typeOfInvestor: '',
    warmIntroProcessCount: null,
    warmIntroProcessDate: ''
  };
}

export function mapAccountOthersData(
  node: AccountOthersNode
): AccountOthersData {
  return {
    accountId: node.id,
    annualRevenue: formatCurrency(node.annualRevenue),
    annualRevenueBase: formatCurrency(node.annualRevenueBase),
    assetsUnderManagement: formatCurrency(node.assetsUnderManagement),
    assetsUnderManagementBase: formatCurrency(node.assetsUnderManagementBase),
    bdc: node.bdc ?? false,
    caes: node.cais ?? false,
    commitmentAmount: formatCurrency(node.commitmentAmount),
    commitmentAmountBase: formatCurrency(node.commitmentAmountBase),
    createdBy: node.createUid?.name ?? '',
    createdOn: node.createDate ?? '',
    creditMarkYes: node.creditMarkYes ?? '',
    crmUrl: node.crmUrl ?? '',
    dateLpPlatformSuppressUnchecked: node.dateLpPlatformSuppressUnchecked ?? '',
    division: node.division ?? '',
    entityImageId: node.entityImageId ?? '',
    esgFocused: node.esgFocused ?? false,
    fax: node.fax ?? '',
    followEmailActivity: node.followEmailActivity ?? false,
    fundManagerType: node.fundManagerType ?? '',
    gdprOptout: node.gdprOptout ?? false,
    geocodeRecord: node.geocodeRecord ?? false,
    iCapital: node.isIcapitalPlatform ?? false,
    importSequenceNumber: node.importSequenceNumber ?? null,
    india2QaCheckedBy: node.india2QaCheckedBy?.name ?? '',
    india2QaCheckedById: node.india2QaCheckedBy?.id ?? '',
    india2QaCheckNeeded: node.india2QaCheckNeeded ?? false,
    indiaQaCheckedBy: node.indiaQaCheckedBy?.name ?? '',
    indiaQaCheckedById: node.indiaQaCheckedBy?.id ?? '',
    indiaQaCheckNeeded: node.indiaQaCheckNeeded ?? false,
    investorCount: node.investorCount ?? null,
    isCais: node.isCais ?? false,
    isIcapitalPlatform: node.isIcapitalPlatform ?? false,
    lastActivityAddedOn: node.lastActivityAddedOn ?? '',
    lastIndia2QaCheckedBy: node.lastIndia2QaCheckedBy?.name ?? '',
    lastIndia2QaCheckedById: node.lastIndia2QaCheckedBy?.id ?? '',
    lastIndia2QaDate: node.india2QaDate ?? '',
    lastIndiaQaCheckedBy: node.lastIndiaQaCheckedBy?.name ?? '',
    lastIndiaQaCheckedById: node.lastIndiaQaCheckedBy?.id ?? '',
    lastIndiaQaDate: node.indiaQaDate ?? '',
    lastNoteAddedOn: node.lastNoteAddedOn ?? '',
    lastSynchedToNotion: node.lastSynchedToNotion ?? '',
    limitedPartner: node.limitedPartner ?? false,
    lpConsultantGeneral: node.lpConsultantGeneralId?.name ?? '',
    lpConsultantGeneralId: node.lpConsultantGeneralId?.id ?? '',
    lpConsultantPe: node.lpConsultantId?.name ?? '',
    lpConsultantPeId: node.lpConsultantId?.id ?? '',
    lpInvestmentAmountMax: formatCurrency(node.lpInvestmentAmmountMax),
    lpInvestmentAmountMaxBase: formatCurrency(node.lpInvestmentAmmountMaxBase),
    lpInvestmentAmountMin: formatCurrency(node.lpInvestmentAmmountMin),
    lpInvestmentAmountMinBase: formatCurrency(node.lpInvestmentAmmountMinBase),
    mainPhone: node.mainPhone ?? '',
    marketingPreferences: (() => {
      const marketingPrefs =
        node.marketingPreferencesIds && node.marketingPreferencesIds.length > 0
          ? node.marketingPreferencesIds[0]
          : null;
      return {
        allowTextMessages: marketingPrefs?.allowTextMessages ?? false,
        doNotAllowBulkEmails: marketingPrefs?.doNotAllowBulkEmails ?? false,
        doNotAllowEmails: marketingPrefs?.doNotAllowEmails ?? false,
        doNotAllowFaxes: marketingPrefs?.doNotAllowFaxes ?? false,
        doNotAllowPhoneCalls: marketingPrefs?.doNotAllowPhoneCalls ?? false,
        marketingOnly: marketingPrefs?.marketingOnly ?? false,
        sendMarketingMaterials: marketingPrefs?.sendMarketingMaterials ?? false
      };
    })(),
    modifiedBy: node.writeUid?.name ?? '',
    modifiedByDelegate: node.modifiedByDelegate?.name ?? '',
    modifiedOn: node.writeDate ?? '',
    nameWithParent: node.nameWithParent ?? '',
    ndaSignedDate: node.ndaSignedDate ?? '',
    nextStepsPlugin: node.nextStepsPlugin ?? '',
    nonSponsorTransactions: node.nonSponsorTransactions ?? false,
    numberOfEmployees: node.numberOfEmployees ?? null,
    ny2QaCheckNeeded: node.ny2QaCheckNeeded ?? false,
    openDeals: node.openDeals ?? null,
    openDealsLastUpdatedTime: node.openDealsLastUpdatedTime ?? '',
    openRevenue: formatCurrency(node.openRevenue),
    openRevenueBase: formatCurrency(node.openRevenueBase),
    openRevenueLastUpdatedTime: node.openRevenueLastUpdatedTime ?? '',
    owner: node.ownerId?.name ?? '',
    ownerId: node.ownerId?.id ?? '',
    ownership: node.ownership ?? '',
    owningUser: node.owningUser?.name ?? '',
    owningUserId: node.owningUser?.id ?? '',
    parentAccountId: node.parentAccountId?.name ?? '',
    placementAgent: node.placementAgent ?? '',
    placementAgentDateAssigned: node.placementAgentDateAssigned ?? '',
    placementAgentGlobal: node.placementAgentGlobal ?? '',
    preferredEmailAddress: node.preferredEmailAddress ?? '',
    preferredMethodOfContact: node.preferredMethodOfContact ?? '',
    preqinFirmId: node.preqinFirmId ?? '',
    primaryContactOriginal: node.primaryContactOriginal ?? '',
    qaCheckCount: '', // Not in query
    reasonForUncheckedLpPlatformSuppress:
      node.reasonForUncheckedLpPlatformSuppress ?? '',
    reverseSolicitationEmailReceivedDate:
      node.reverseSolicitationEmailReceivedDate ?? '',
    revolverNonAbl: node.revolverNonAbl ?? false,
    sbicQualifying: node.sbicQualifying ?? false,
    sicCode: node.sicCode ?? '',
    soundex: node.soundex ?? '',
    sponsorInformation: node.sponsorInformation ?? '',
    sponsorTransactions: node.sponsorTransactions ?? false,
    sqlDate: node.sqlDate ?? '',
    summaryNotes: node.summaryNotes ?? '',
    tickerSymbol: node.tickerSymbol ?? '',
    totalActivities: node.totalActivities ?? null,
    totalContacts: node.totalContacts ?? null,
    totalContactsLastUpdatedOn: node.totalContactsLastUpdatedOn ?? '',
    totalInvestorOpportunities: node.totalInvestorOpportunities ?? null,
    totalInvestorOpportunitiesLastUpdatedOn:
      node.totalInvestorOpportunitiesLastUpdatedOn ?? '',
    totalNotes: node.totalNotes ?? null,
    totalOpenIos: node.totalOpenIos ?? null,
    typeOfInvestor: node.typeOfInvestor ?? '',
    warmIntroProcessCount: node.warmIntroProcessCount ?? null,
    warmIntroProcessDate: node.warmIntroProcessDate ?? ''
  };
}

// Format currency helper
// Note: formatCurrency is imported from @/lib/utils
